import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, FileText, Info, GitMerge, Pencil, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import {
  ComponentEditor,
  type ComponentEditorHandle,
} from '@/shared/components/packaging/ComponentEditor'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  type PackagingAuditKind,
  type PackagingComponentInput,
  useGetPackagingItem,
  useGetPackagingItemMergedSources,
  useGetPackagingSupplier,
  useUpdatePackagingItem,
} from '@/shared/hooks/api/packagingSuppliers'
import { useGetPackedItem } from '@/shared/hooks/api/packedItemsOverview'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { parsePackagingItemChangeHistory } from '@/shared/packagingItemChangeHistory'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { LexItemDocumentsPanel } from './LexItemDocumentsPanel'
import { PackagingItemAuditRunSheet } from './PackagingItemAuditRunSheet'
import { PackagingItemAuditsTab } from './PackagingItemAuditsTab'
import { PackagingItemChangeHistoryTab } from './PackagingItemChangeHistoryTab'
import { PackagingItemEditDialog } from './PackagingItemEditDialog'
import { PackagingItemHeavyMetalsAuditRunSheet } from './PackagingItemHeavyMetalsAuditRunSheet'
import { PackagingItemHeavyMetalsAuditTab } from './PackagingItemHeavyMetalsAuditTab'
import { PackagingItemManualOverrideSheet } from './PackagingItemManualOverrideSheet'
import { isManualOverrideEntry } from './packagingAuditOverrideValidation'
import { PackagingItemSvhcAuditRunSheet } from './PackagingItemSvhcAuditRunSheet'
import { PackagingItemSvhcAuditTab } from './PackagingItemSvhcAuditTab'
import { PackagingItemMetadataItemGrid } from './PackagingItemMetadataItemGrid'
import { PackagingItemMinimisationAssessmentDocumentSheet } from './PackagingItemMinimisationAssessmentDocumentSheet'
import { PackagingItemMinimisationRunSheet } from './PackagingItemMinimisationRunSheet'
import { PackagingItemMinimisationTab } from './PackagingItemMinimisationTab'
import { PackagingItemOutputDocumentsTab } from './PackagingItemOutputDocumentsTab'
import { PackagingItemRecyclabilityAssessmentDocumentSheet } from './PackagingItemRecyclabilityAssessmentDocumentSheet'
import { PackagingItemRecyclabilityRunSheet } from './PackagingItemRecyclabilityRunSheet'
import { PackagingItemRecyclabilityTab } from './PackagingItemRecyclabilityTab'
import {
  getPackagingItemDetailPackedItemId,
  parsePackagingItemDetailTab,
  setPackagingItemDetailTabSearchParams,
  type PackagingItemDetailTab,
} from './packagingItemDetailNavigation'
import { resolveRecyclabilityGradeFromAssessment } from './resolveRecyclabilityGradeFromAssessment'
import { usePackagingSuppliersComponentEditorLabels } from './usePackagingSuppliersComponentEditorLabels'

function toComponentInputs(
  components: Array<{
    name: string
    material: PackagingComponentInput['material']
    weightInGrams: number
    recyclable: boolean
    recyclingStream?: PackagingComponentInput['recyclingStream']
    recyclingPct: string
    reusable: boolean
    notes?: string | null
  }>
): PackagingComponentInput[] {
  return components.map((component) => ({
    name: component.name,
    material: component.material,
    weightInGrams: component.weightInGrams,
    recyclable: component.recyclable,
    recyclingStream: component.recyclingStream ?? null,
    recyclingPct: component.recyclingPct,
    reusable: component.reusable,
    notes: component.notes ?? null,
  }))
}

export function PackagingItemDetailsPage() {
  const { packagingSupplierId = '', itemId = '' } = useParams<{
    packagingSupplierId: string
    itemId: string
  }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const contextPackedItemId = getPackagingItemDetailPackedItemId(searchParams)
  const activeTab = parsePackagingItemDetailTab(searchParams)
  const auditsTabPath = useMemo(() => {
    const nextSearch = setPackagingItemDetailTabSearchParams(
      searchParams,
      'audits'
    )
    const query = nextSearch.toString()
    if (query === '') {
      return `${location.pathname}?tab=audits`
    }
    return `${location.pathname}?${query}`
  }, [location.pathname, searchParams])
  const recyclabilityTabPath = useMemo(() => {
    const nextSearch = setPackagingItemDetailTabSearchParams(
      searchParams,
      'recyclability'
    )
    const query = nextSearch.toString()
    if (query === '') {
      return `${location.pathname}?tab=recyclability`
    }
    return `${location.pathname}?${query}`
  }, [location.pathname, searchParams])
  const declarationOfConformityTabPath = useMemo(() => {
    const nextSearch = setPackagingItemDetailTabSearchParams(
      searchParams,
      'output-docs'
    )
    const query = nextSearch.toString()
    if (query === '') {
      return `${location.pathname}?tab=output-docs`
    }
    return `${location.pathname}?${query}`
  }, [location.pathname, searchParams])
  const { t, i18n } = useTranslation(['packagingSuppliers', 'common'])
  const canWriteOrgData = useCanWriteOrgData()
  const {
    data: item,
    isLoading,
    isError,
  } = useGetPackagingItem(packagingSupplierId, itemId, {
    refetchInterval: (query) => {
      const outputDocuments = query.state.data?.outputDocuments ?? []
      const hasInFlightOutputDocument = outputDocuments.some(
        (document) =>
          document.status === 'PENDING' || document.status === 'PROCESSING'
      )
      if (hasInFlightOutputDocument) {
        return 1500
      }
      return false
    },
  })
  const { data: supplier } = useGetPackagingSupplier(packagingSupplierId)
  const { data: contextPackedItem } = useGetPackedItem(contextPackedItemId)
  const updateItem = useUpdatePackagingItem(packagingSupplierId, itemId)
  const { data: session } = useGetUserSession()
  const isArtemAdmin = session?.isArtemAdmin === true
  // Recyclability / minimisation tabs, DoC, and HM/SVHC audits are available to all
  // org users. Run actions require write access (`canWriteOrgData`).
  // Recyclability / minimisation document generate-download and document ingestion
  // remain @artem.earth-only (`isArtemAdmin` is derived from email ending in
  // @artem.earth).
  const canAccessPackagingAssessments = true
  const canRunPackagingAssessments = canWriteOrgData
  const canAccessPackagingAssessmentDocuments =
    isArtemAdmin && canRunPackagingAssessments
  const visibleTab = activeTab
  const { data: mergedSourcesData, isLoading: mergedSourcesLoading } =
    useGetPackagingItemMergedSources(packagingSupplierId, itemId, {
      enabled: isArtemAdmin,
    })
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [auditRunSheetOpen, setAuditRunSheetOpen] = useState(false)
  const [heavyMetalsAuditRunSheetOpen, setHeavyMetalsAuditRunSheetOpen] =
    useState(false)
  const [svhcAuditRunSheetOpen, setSvhcAuditRunSheetOpen] = useState(false)
  const [manualOverrideSheetOpen, setManualOverrideSheetOpen] = useState(false)
  const [manualOverrideAuditKind, setManualOverrideAuditKind] =
    useState<PackagingAuditKind>('submission')

  const openManualOverrideSheet = (auditKind: PackagingAuditKind) => {
    setManualOverrideAuditKind(auditKind)
    setManualOverrideSheetOpen(true)
  }
  const [recyclabilityRunSheetOpen, setRecyclabilityRunSheetOpen] =
    useState(false)
  const [recyclabilityDocumentSheetOpen, setRecyclabilityDocumentSheetOpen] =
    useState(false)
  const [minimisationRunSheetOpen, setMinimisationRunSheetOpen] =
    useState(false)
  const [minimisationDocumentSheetOpen, setMinimisationDocumentSheetOpen] =
    useState(false)
  const [completeWarningOpen, setCompleteWarningOpen] = useState(false)
  const [components, setComponents] = useState<PackagingComponentInput[]>([])
  const componentEditorRef = useRef<ComponentEditorHandle>(null)

  const showPackedItemTrail =
    contextPackedItem != null &&
    contextPackedItem.packagingItems.some(
      (packagingItem) => packagingItem.id === itemId
    )

  useEffect(() => {
    if (!item) {
      return
    }
    setComponents(toComponentInputs(item.components ?? []))
  }, [item])

  const ppwrComplianceLabels = {
    heavyMetals: t('ppwrCompliance.heavyMetals'),
    substancesOfConcern: t('ppwrCompliance.substancesOfConcern'),
    none: t('ppwrCompliance.none'),
    sufficientlyMinimized: t('ppwrCompliance.sufficientlyMinimized'),
    heavyMetalsSufficientlyMinimizedHelp: t(
      'ppwrCompliance.heavyMetalsSufficientlyMinimizedHelp'
    ),
    notSufficientlyMinimized: t('ppwrCompliance.notSufficientlyMinimized'),
  }
  const componentEditorLabels = usePackagingSuppliersComponentEditorLabels()
  const manufacturerLabels = {
    question: t('items.manufacturer.question'),
    optionYes: t('items.manufacturer.optionYes'),
    optionNo: t('items.manufacturer.optionNo'),
    manufacturerName: t('items.manufacturer.manufacturerName'),
    manufacturerAddress: t('items.manufacturer.manufacturerAddress'),
    manufacturerNameRequired: t('items.manufacturer.manufacturerNameRequired'),
    manufacturerAddressRequired: t(
      'items.manufacturer.manufacturerAddressRequired'
    ),
  }

  const shell = (content: ReactNode) => (
    <>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <Main className='flex flex-1 flex-col gap-6 px-6'>{content}</Main>
      {canWriteOrgData && packagingSupplierId && itemId && item ? (
        <>
          <PackagingItemAuditRunSheet
            supplierId={packagingSupplierId}
            itemId={itemId}
            open={auditRunSheetOpen}
            onOpenChange={setAuditRunSheetOpen}
            latestEntryIsManualOverride={
              item.fieldCrossCheckHistory?.[0] != null &&
              isManualOverrideEntry(item.fieldCrossCheckHistory[0])
            }
          />
          <PackagingItemHeavyMetalsAuditRunSheet
            supplierId={packagingSupplierId}
            itemId={itemId}
            open={heavyMetalsAuditRunSheetOpen}
            onOpenChange={setHeavyMetalsAuditRunSheetOpen}
            latestEntryIsManualOverride={
              item.heavyMetalsAuditHistory?.[0] != null &&
              isManualOverrideEntry(item.heavyMetalsAuditHistory[0])
            }
          />
          <PackagingItemSvhcAuditRunSheet
            supplierId={packagingSupplierId}
            itemId={itemId}
            open={svhcAuditRunSheetOpen}
            onOpenChange={setSvhcAuditRunSheetOpen}
            latestEntryIsManualOverride={
              item.svhcAuditHistory?.[0] != null &&
              isManualOverrideEntry(item.svhcAuditHistory[0])
            }
          />
          <PackagingItemManualOverrideSheet
            supplierId={packagingSupplierId}
            itemId={itemId}
            auditKind={manualOverrideAuditKind}
            open={manualOverrideSheetOpen}
            onOpenChange={setManualOverrideSheetOpen}
            fieldCrossCheckHistory={item.fieldCrossCheckHistory ?? []}
            heavyMetalsAuditHistory={item.heavyMetalsAuditHistory ?? []}
            svhcAuditHistory={item.svhcAuditHistory ?? []}
          />
        </>
      ) : null}
      {canAccessPackagingAssessmentDocuments && item ? (
        <PackagingItemRecyclabilityAssessmentDocumentSheet
          supplierId={packagingSupplierId}
          itemId={itemId}
          itemName={item.name}
          itemDescription={item.description}
          intendedUse={item.intendedUse}
          packagingIdentificationNumber={
            item.sku?.trim() || item.packedItemSku?.trim() || item.id
          }
          manufacturerName={item.manufacturerName ?? ''}
          manufacturerAddress={item.manufacturerAddress ?? ''}
          components={item.components}
          history={item.recyclabilityAssessmentHistory}
          open={recyclabilityDocumentSheetOpen}
          onOpenChange={setRecyclabilityDocumentSheetOpen}
        />
      ) : null}
      {canRunPackagingAssessments && packagingSupplierId && itemId ? (
        <PackagingItemRecyclabilityRunSheet
          supplierId={packagingSupplierId}
          itemId={itemId}
          open={recyclabilityRunSheetOpen}
          onOpenChange={setRecyclabilityRunSheetOpen}
        />
      ) : null}
      {canAccessPackagingAssessmentDocuments && item ? (
        <PackagingItemMinimisationAssessmentDocumentSheet
          supplierId={packagingSupplierId}
          itemId={itemId}
          itemName={item.name}
          itemDescription={item.description}
          intendedUse={item.intendedUse}
          packagingIdentificationNumber={
            item.sku?.trim() || item.packedItemSku?.trim() || item.id
          }
          manufacturerName={item.manufacturerName ?? ''}
          manufacturerAddress={item.manufacturerAddress ?? ''}
          history={item.minimisationAssessmentHistory}
          open={minimisationDocumentSheetOpen}
          onOpenChange={setMinimisationDocumentSheetOpen}
        />
      ) : null}
      {canRunPackagingAssessments && packagingSupplierId && itemId ? (
        <PackagingItemMinimisationRunSheet
          supplierId={packagingSupplierId}
          itemId={itemId}
          open={minimisationRunSheetOpen}
          onOpenChange={setMinimisationRunSheetOpen}
        />
      ) : null}
    </>
  )

  const handleDetailTabChange = (value: string | number | null) => {
    if (value == null) {
      return
    }
    let nextTab: PackagingItemDetailTab = 'overview'
    if (value === 'audits') {
      nextTab = 'audits'
    }
    if (value === 'report-audit') {
      nextTab = 'heavy-metals-audit'
    }
    if (value === 'heavy-metals-audit') {
      nextTab = 'heavy-metals-audit'
    }
    if (value === 'svhc-audit') {
      nextTab = 'svhc-audit'
    }
    if (canAccessPackagingAssessments && value === 'recyclability') {
      nextTab = 'recyclability'
    }
    if (canAccessPackagingAssessments && value === 'minimisation') {
      nextTab = 'minimisation'
    }
    if (value === 'changelog') {
      nextTab = 'changelog'
    }
    if (value === 'output-docs') {
      nextTab = 'output-docs'
    }
    if (nextTab === activeTab) {
      return
    }
    setSearchParams(
      setPackagingItemDetailTabSearchParams(searchParams, nextTab),
      { replace: true }
    )
  }

  if (isLoading) {
    return shell(
      <div className='py-12 text-center text-muted-foreground'>
        {t('itemDetail.loading')}
      </div>
    )
  }

  if (isError || !item) {
    return shell(
      <div className='space-y-3'>
        <h2 className='text-2xl font-bold tracking-tight'>
          {t('itemDetail.notFound')}
        </h2>
        <Link
          to={`/packaging/suppliers/${packagingSupplierId}`}
          className='text-sm text-primary underline'
        >
          {t('itemDetail.backToSupplier')}
        </Link>
      </div>
    )
  }

  const weightPercentages = item.components?.map(
    (component) => component.weightAsPercentage ?? 0
  )

  const handleComponentsChange = async (
    nextComponents: PackagingComponentInput[]
  ) => {
    if (!canWriteOrgData) {
      return
    }
    const previous = components
    setComponents(nextComponents)
    try {
      const updated = await updateItem.mutateAsync({
        components: nextComponents,
      })
      setComponents(toComponentInputs(updated.components ?? []))
    } catch {
      setComponents(previous)
    }
  }

  const totalWeight = item.totalWeight
  const showTotalWeight = totalWeight !== undefined && totalWeight > 0

  const linkedPackedItems = item.packagedItems ?? []
  const mergedInto = item.mergedIntoPackagingItem ?? null
  const isConsolidatedItem = isArtemAdmin && mergedInto != null
  const canEditItem = canWriteOrgData && !isConsolidatedItem

  const missingRequiredTypes =
    item.documentRequirements?.missingRequiredTypes ?? []
  const hasNoComponents = components.length === 0
  const hasMissingMandatoryDocuments = missingRequiredTypes.length > 0
  const shouldWarnBeforeComplete =
    hasNoComponents || hasMissingMandatoryDocuments

  const documentTypeLabels: Record<string, string> = {
    DIELINE_DIAGRAM: t('itemDetail.documentTypes.dielineDiagram'),
    PACKAGING_PHOTOS: t('itemDetail.documentTypes.packagingPhotos'),
    LABEL_PHOTOS: t('itemDetail.documentTypes.labelPhotos'),
    SDS_OR_BOM: t('itemDetail.documentTypes.sdsOrBom'),
    HEAVY_METALS_TEST_REPORT: t(
      'itemDetail.documentTypes.heavyMetalsTestReport'
    ),
    HEAVY_METALS_CONFORMITY_STATEMENT: t(
      'itemDetail.documentTypes.heavyMetalsConformityStatement'
    ),
    SUBSTANCES_OF_CONCERN_TEST_REPORT: t(
      'itemDetail.documentTypes.substancesOfConcernTestReport'
    ),
    SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT: t(
      'itemDetail.documentTypes.substancesOfConcernConformityStatement'
    ),
    OTHER: t('itemDetail.documentTypes.other'),
    OTHER_TEST_REPORT: t('itemDetail.documentTypes.otherTestReport'),
  }

  const markCompleted = async () => {
    await updateItem.mutateAsync({ completed: true })
    setCompleteWarningOpen(false)
  }

  const handleCompletionToggle = async () => {
    if (item.completedAt != null) {
      await updateItem.mutateAsync({ completed: false })
      return
    }
    if (shouldWarnBeforeComplete) {
      setCompleteWarningOpen(true)
      return
    }
    await markCompleted()
  }

  const locale = i18n.language.startsWith('zh') ? 'zh-CN' : 'en-GB'
  const completedDateLabel =
    item.completedAt != null
      ? t('itemDetail.statusCompletedOn', {
          date: new Date(item.completedAt).toLocaleString(locale),
        })
      : null

  let markCompleteButtonVariant: 'outline' | 'warning' | 'default' = 'default'
  if (item.completedAt != null) {
    markCompleteButtonVariant = 'outline'
  } else if (shouldWarnBeforeComplete) {
    markCompleteButtonVariant = 'warning'
  }

  let markCompleteButtonLabel = t('itemDetail.markCompleted')
  if (item.completedAt != null) {
    markCompleteButtonLabel = t('itemDetail.markPending')
  }

  let statusBadgeLabel = t('itemDetail.statusPending')
  if (isConsolidatedItem) {
    statusBadgeLabel = t('packagingItems.statusConsolidated')
  } else if (item.completedAt) {
    statusBadgeLabel = t('itemDetail.statusCompleted')
  }

  let statusBadgeVariant: 'success' | 'secondary' = 'secondary'
  if (!isConsolidatedItem && item.completedAt) {
    statusBadgeVariant = 'success'
  }

  const assessmentGrade = resolveRecyclabilityGradeFromAssessment(
    item.recyclabilityAssessmentHistory
  )
  let assessmentGradeLabel: string | null = null
  let assessmentGradeTooltip = t(
    'itemDetail.recyclability.assessmentGradeTooltipNoPct'
  )
  let recyclabilityAlertVariant:
    | 'success'
    | 'info'
    | 'warning'
    | 'destructive' = 'info'
  let recyclabilitySummaryRecyclable = t('itemDetail.recyclability.recyclable')
  let recyclabilitySummaryClass = '—'
  if (assessmentGrade != null) {
    if (assessmentGrade.grade === 'NON_RECYCLABLE') {
      assessmentGradeLabel = t(
        'itemDetail.recyclability.technicallyNonRecyclable'
      )
      recyclabilityAlertVariant = 'destructive'
      recyclabilitySummaryRecyclable = t(
        'itemDetail.recyclability.technicallyNonRecyclable'
      )
    } else {
      assessmentGradeLabel = t('itemDetail.recyclability.gradeLabel', {
        grade: assessmentGrade.grade,
      })
      recyclabilitySummaryClass = assessmentGrade.grade
      if (assessmentGrade.grade === 'A') {
        recyclabilityAlertVariant = 'success'
      } else if (assessmentGrade.grade === 'C') {
        recyclabilityAlertVariant = 'warning'
      }
    }
    if (assessmentGrade.recyclableContentPct != null) {
      assessmentGradeTooltip = t(
        'itemDetail.recyclability.assessmentGradeTooltip',
        {
          pct: assessmentGrade.recyclableContentPct.toFixed(1),
        }
      )
    }
  }

  const handleUnlinkPackedItem = async (packedItemId: string) => {
    if (!canWriteOrgData) {
      return
    }
    const remainingIds = linkedPackedItems
      .map((packedItem) => packedItem.id)
      .filter((id) => id !== packedItemId)
    if (remainingIds.length === 0) {
      await updateItem.mutateAsync({
        packagesOwnProduct: false,
        packagedItemIds: [],
      })
      return
    }
    await updateItem.mutateAsync({
      packagedItemIds: remainingIds,
    })
  }

  const latestAuditRun = item.fieldCrossCheckHistory?.[0] ?? null
  const failedAuditChecks =
    latestAuditRun != null && !isManualOverrideEntry(latestAuditRun)
      ? latestAuditRun.checks.filter((check) => !check.passed)
      : []
  const showLatestAuditIssuesAlert = failedAuditChecks.length > 0
  const showRecyclabilityAlert =
    canAccessPackagingAssessments &&
    assessmentGrade != null &&
    assessmentGradeLabel != null
  const showOverviewAlerts =
    showLatestAuditIssuesAlert || showRecyclabilityAlert

  return shell(
    <>
      <div className='space-y-2'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div className='flex flex-wrap items-center gap-2'>
            <h2 className='text-2xl font-bold tracking-tight'>{item.name}</h2>
            <Badge
              variant={statusBadgeVariant}
              className={isConsolidatedItem ? 'gap-1' : undefined}
            >
              {isConsolidatedItem ? (
                <GitMerge className='size-3' aria-hidden />
              ) : null}
              {statusBadgeLabel}
            </Badge>
          </div>
          {canEditItem && (
            <div className='flex flex-wrap items-center gap-3'>
              {completedDateLabel ? (
                <p className='text-sm text-muted-foreground'>
                  {completedDateLabel}
                </p>
              ) : null}
              <Button
                type='button'
                variant={markCompleteButtonVariant}
                size='sm'
                disabled={updateItem.isPending}
                onClick={() => {
                  void handleCompletionToggle()
                }}
              >
                {markCompleteButtonVariant === 'warning' ? (
                  <AlertTriangle className='size-3.5' aria-hidden />
                ) : null}
                {markCompleteButtonLabel}
              </Button>
            </div>
          )}
        </div>
        <Breadcrumb>
          <BreadcrumbList>
            {showPackedItemTrail && contextPackedItem ? (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to='/packaging/packed-items'>
                      {t('tabs.packedItems')}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      to={`/packaging/packed-items/${contextPackedItem.id}`}
                    >
                      {contextPackedItem.name}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            ) : (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to='/packaging/suppliers'>{t('tabs.suppliers')}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/packaging/suppliers/${packagingSupplierId}`}>
                      {supplier?.name ?? t('itemDetail.backToSupplier')}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className='max-w-[min(100%,28rem)] truncate'>
                {item.name}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {isConsolidatedItem && mergedInto != null && (
          <Alert variant='warning'>
            <AlertTitle>{t('packagingItems.statusConsolidated')}</AlertTitle>
            <AlertDescription>
              <p>
                {t('itemDetail.mergedIntoBannerLead')}{' '}
                <Link
                  to={`/packaging/suppliers/${packagingSupplierId}/items/${mergedInto.id}`}
                  className='font-medium underline decoration-dotted underline-offset-2'
                >
                  {mergedInto.name}
                </Link>
              </p>
            </AlertDescription>
          </Alert>
        )}
      </div>

      <Tabs
        value={visibleTab}
        onValueChange={handleDetailTabChange}
        className='flex flex-col gap-6'
      >
        <TabsList>
          <TabsTrigger value='overview'>
            {t('itemDetail.tabs.overview')}
          </TabsTrigger>
          <TabsTrigger value='audits'>
            {t('itemDetail.tabs.audits')}
          </TabsTrigger>
          <TabsTrigger value='heavy-metals-audit'>
            {t('itemDetail.tabs.heavyMetalsAudit')}
          </TabsTrigger>
          <TabsTrigger value='svhc-audit'>
            {t('itemDetail.tabs.svhcAudit')}
          </TabsTrigger>
          {canAccessPackagingAssessments && (
            <TabsTrigger value='recyclability'>
              {t('itemDetail.tabs.recyclability')}
            </TabsTrigger>
          )}
          {canAccessPackagingAssessments && (
            <TabsTrigger value='minimisation'>
              {t('itemDetail.tabs.minimisation')}
            </TabsTrigger>
          )}
          <TabsTrigger value='output-docs'>
            {t('itemDetail.tabs.outputDocs')}
          </TabsTrigger>
          <TabsTrigger value='changelog'>
            {t('itemDetail.tabs.changeHistory')}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value='overview'
          className='flex flex-col divide-y divide-border'
        >
          {showOverviewAlerts && (
            <div className='space-y-4 pb-8'>
              {showLatestAuditIssuesAlert && (
                <Alert variant='destructive'>
                  <AlertTitle>
                    {t('itemDetail.audits.latestRunIssuesTitle')}
                  </AlertTitle>
                  <AlertDescription>
                    <p>{t('itemDetail.audits.latestRunIssuesLead')}</p>
                    <ul className='mt-2 list-disc space-y-0.5 ps-5 text-sm'>
                      {failedAuditChecks.map((check) => (
                        <li key={check.code}>
                          {t(`fieldCrossCheck.checks.${check.code}`)}
                        </li>
                      ))}
                    </ul>
                    <p className='mt-3'>
                      <Link
                        to={auditsTabPath}
                        className='font-medium underline underline-offset-4'
                      >
                        {t('itemDetail.audits.viewAuditsTab')}
                      </Link>
                    </p>
                  </AlertDescription>
                </Alert>
              )}
              {showRecyclabilityAlert && (
                <Alert variant={recyclabilityAlertVariant}>
                  <AlertTitle>
                    {t('itemDetail.recyclability.latestRunSummaryTitle')}
                  </AlertTitle>
                  <AlertDescription>
                    <p>
                      {t('itemDetail.recyclability.latestRunSummaryLead', {
                        recyclable: recyclabilitySummaryRecyclable,
                        class: recyclabilitySummaryClass,
                      })}
                    </p>
                    <p className='mt-2'>{assessmentGradeTooltip}</p>
                    {canAccessPackagingAssessments ? (
                      <p className='mt-3'>
                        <Link
                          to={recyclabilityTabPath}
                          className='font-medium underline underline-offset-4'
                        >
                          {t('itemDetail.recyclability.viewRecyclabilityTab')}
                        </Link>
                      </p>
                    ) : null}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <section className='space-y-3 py-8 first:pt-0 last:pb-0'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <h3 className='text-lg font-semibold'>
                {t('itemDetail.sections.details')}
              </h3>
              <div className='flex flex-wrap items-center gap-2'>
                <Button type='button' variant='outline' size='sm' asChild>
                  <Link to={declarationOfConformityTabPath}>
                    <FileText className='size-3.5' />
                    {t('itemDetail.declarationOfConformity.openTab')}
                  </Link>
                </Button>
                {canEditItem && (
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setEditDialogOpen(true)}
                  >
                    <Pencil className='size-3.5' />
                    {t('itemDetail.editDetails')}
                  </Button>
                )}
              </div>
            </div>
            <PackagingItemMetadataItemGrid
              item={item}
              manufacturerLabels={manufacturerLabels}
              ppwrComplianceLabels={ppwrComplianceLabels}
            />
          </section>

          {isArtemAdmin && (
            <section className='space-y-3 py-8 first:pt-0 last:pb-0'>
              <h3 className='flex items-center gap-2 text-lg font-semibold'>
                <GitMerge
                  className='size-4 text-muted-foreground'
                  aria-hidden
                />
                {t('itemDetail.mergedSourceItems')}
              </h3>
              {mergedSourcesLoading ? (
                <p className='text-sm text-muted-foreground'>
                  {t('itemDetail.loadingMergedSourceItems')}
                </p>
              ) : (mergedSourcesData?.items.length ?? 0) === 0 ? (
                <p className='text-sm text-muted-foreground'>
                  {t('itemDetail.noMergedSourceItems')}
                </p>
              ) : (
                <div className='overflow-x-auto rounded-md border'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {t('details.partPackaging.columnName')}
                        </TableHead>
                        <TableHead>{t('packedItems.columnSku')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mergedSourcesData?.items.map((mergedSource) => (
                        <TableRow key={mergedSource.id}>
                          <TableCell>
                            <Link
                              to={`/packaging/suppliers/${packagingSupplierId}/items/${mergedSource.id}`}
                              className='font-medium text-foreground underline decoration-dotted underline-offset-2'
                            >
                              {mergedSource.name}
                            </Link>
                          </TableCell>
                          <TableCell>
                            {mergedSource.sku?.trim()
                              ? mergedSource.sku
                              : t('common:notAvailable')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>
          )}

          <section className='space-y-3 py-8 first:pt-0 last:pb-0'>
            <h3 className='text-lg font-semibold'>
              {t('itemDetail.linkedPackedItems')}
            </h3>
            {linkedPackedItems.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                {t('itemDetail.noLinkedPackedItems')}
              </p>
            ) : (
              <div className='overflow-x-auto rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {t('details.partPackaging.columnName')}
                      </TableHead>
                      <TableHead>{t('packedItems.columnSku')}</TableHead>
                      {canEditItem && (
                        <TableHead className='text-end'>
                          {t('list.columnActions')}
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedPackedItems.map((packedItem) => (
                      <TableRow key={packedItem.id}>
                        <TableCell>
                          <Link
                            to={`/packaging/packed-items/${packedItem.id}`}
                            className='font-medium text-foreground underline decoration-dotted underline-offset-2'
                          >
                            {packedItem.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {packedItem.sku?.trim()
                            ? packedItem.sku
                            : t('common:notAvailable')}
                        </TableCell>
                        {canEditItem && (
                          <TableCell className='text-end'>
                            <Button
                              type='button'
                              variant='outline'
                              size='sm'
                              disabled={updateItem.isPending}
                              onClick={() => {
                                void handleUnlinkPackedItem(packedItem.id)
                              }}
                            >
                              {t('itemDetail.unlinkPackedItem')}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>

          <section className='space-y-3 py-8 first:pt-0 last:pb-0'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <div className='flex flex-wrap items-center gap-2'>
                <h3 className='text-lg font-semibold'>
                  {t('itemDetail.sections.components')}
                </h3>
                {showTotalWeight && totalWeight != null && (
                  <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                    <span>
                      {t('itemDetail.totalWeight', {
                        weight: totalWeight.toFixed(2),
                      })}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type='button'
                          className='inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground'
                          aria-label={t(
                            'itemDetail.totalWeightAutoCalculatedTooltip'
                          )}
                        >
                          <Info className='size-3.5' aria-hidden />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side='top' className='max-w-xs'>
                        {t('itemDetail.totalWeightAutoCalculatedTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
              {canEditItem && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='gap-1'
                  disabled={updateItem.isPending}
                  onClick={() => {
                    componentEditorRef.current?.openAddSheet()
                  }}
                >
                  <Plus className='size-3.5' />
                  {componentEditorLabels.addComponent}
                </Button>
              )}
            </div>
            <ComponentEditor
              ref={componentEditorRef}
              components={components}
              onChange={(nextComponents) => {
                void handleComponentsChange(nextComponents)
              }}
              isSaving={updateItem.isPending}
              readOnly={!canEditItem}
              showAddButton={false}
              weightPercentages={
                components.length === (item.components?.length ?? 0)
                  ? weightPercentages
                  : undefined
              }
              labels={componentEditorLabels}
            />
          </section>

          <section className='py-8 first:pt-0 last:pb-0'>
            <LexItemDocumentsPanel
              supplierId={packagingSupplierId}
              itemId={itemId}
            />
          </section>
        </TabsContent>

        <TabsContent value='audits'>
          <PackagingItemAuditsTab
            history={item.fieldCrossCheckHistory ?? []}
            locale={i18n.language}
            showRunAudit={canWriteOrgData}
            onRunAuditClick={() => {
              setAuditRunSheetOpen(true)
            }}
            showManualOverride={
              canWriteOrgData && (item.fieldCrossCheckHistory?.length ?? 0) > 0
            }
            onManualOverrideClick={() => {
              openManualOverrideSheet('submission')
            }}
          />
        </TabsContent>

        <TabsContent value='heavy-metals-audit'>
          <PackagingItemHeavyMetalsAuditTab
            history={item.heavyMetalsAuditHistory ?? []}
            locale={i18n.language}
            showRunAudit={canWriteOrgData}
            onRunAuditClick={() => {
              setHeavyMetalsAuditRunSheetOpen(true)
            }}
            showManualOverride={
              canWriteOrgData && (item.heavyMetalsAuditHistory?.length ?? 0) > 0
            }
            onManualOverrideClick={() => {
              openManualOverrideSheet('heavy_metals')
            }}
          />
        </TabsContent>

        <TabsContent value='svhc-audit'>
          <PackagingItemSvhcAuditTab
            history={item.svhcAuditHistory ?? []}
            locale={i18n.language}
            showRunAudit={canWriteOrgData}
            onRunAuditClick={() => {
              setSvhcAuditRunSheetOpen(true)
            }}
            showManualOverride={
              canWriteOrgData && (item.svhcAuditHistory?.length ?? 0) > 0
            }
            onManualOverrideClick={() => {
              openManualOverrideSheet('svhc')
            }}
          />
        </TabsContent>

        {canAccessPackagingAssessments && (
          <TabsContent value='recyclability'>
            <PackagingItemRecyclabilityTab
              history={item.recyclabilityAssessmentHistory ?? []}
              locale={i18n.language}
              showRunAssessment={canRunPackagingAssessments}
              onRunAssessmentClick={() => {
                setRecyclabilityRunSheetOpen(true)
              }}
              showPreviewDocument={canAccessPackagingAssessmentDocuments}
              onPreviewDocumentClick={() => {
                setRecyclabilityDocumentSheetOpen(true)
              }}
            />
          </TabsContent>
        )}

        {canAccessPackagingAssessments && (
          <TabsContent value='minimisation'>
            <PackagingItemMinimisationTab
              history={item.minimisationAssessmentHistory ?? []}
              locale={i18n.language}
              showRunAssessment={canRunPackagingAssessments}
              onRunAssessmentClick={() => {
                setMinimisationRunSheetOpen(true)
              }}
              showPreviewDocument={canAccessPackagingAssessmentDocuments}
              onPreviewDocumentClick={() => {
                setMinimisationDocumentSheetOpen(true)
              }}
            />
          </TabsContent>
        )}

        <TabsContent value='output-docs'>
          <PackagingItemOutputDocumentsTab
            packagingSupplierId={packagingSupplierId}
            item={item}
          />
        </TabsContent>

        <TabsContent value='changelog'>
          <PackagingItemChangeHistoryTab
            history={parsePackagingItemChangeHistory(item.changeHistory)}
            locale={i18n.language}
          />
        </TabsContent>
      </Tabs>

      {canEditItem && (
        <PackagingItemEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          item={item}
          supplier={supplier}
          isSaving={updateItem.isPending}
          onSave={async (payload) => {
            await updateItem.mutateAsync(payload)
            setEditDialogOpen(false)
          }}
        />
      )}

      {canEditItem && (
        <AlertDialog
          open={completeWarningOpen}
          onOpenChange={setCompleteWarningOpen}
        >
          <AlertDialogContent className='border-amber-200 sm:max-w-lg dark:border-amber-900/60'>
            <AlertDialogHeader>
              <div className='mx-auto flex size-12 items-center justify-center rounded-full bg-amber-100 text-amber-700 sm:mx-0 dark:bg-amber-950/80 dark:text-amber-300'>
                <AlertTriangle className='size-6' aria-hidden />
              </div>
              <AlertDialogTitle className='text-amber-900 dark:text-amber-100'>
                {t('itemDetail.completeWarning.title')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('itemDetail.completeWarning.intro')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className='space-y-3 rounded-lg border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100'>
              <p className='text-amber-900/80 dark:text-amber-100/80'>
                {t('itemDetail.completeWarning.recommend')}
              </p>
              <ul className='list-disc space-y-1 ps-5'>
                {hasNoComponents ? (
                  <li>{t('itemDetail.completeWarning.missingComponents')}</li>
                ) : null}
                {hasMissingMandatoryDocuments ? (
                  <li>
                    <span>
                      {t('itemDetail.completeWarning.missingDocuments')}
                    </span>
                    <ul className='mt-1 list-disc space-y-1 ps-5'>
                      {missingRequiredTypes.map((documentType) => (
                        <li key={documentType}>
                          {documentTypeLabels[documentType] ?? documentType}
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : null}
              </ul>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={updateItem.isPending}>
                {t('itemDetail.completeWarning.goBack')}
              </AlertDialogCancel>
              <Button
                type='button'
                variant='warning'
                disabled={updateItem.isPending}
                onClick={() => {
                  void markCompleted()
                }}
              >
                <AlertTriangle className='size-4' aria-hidden />
                {t('itemDetail.completeWarning.continueAnyway')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  )
}
